import { Injectable, inject } from '@angular/core'
import { Router, ActivatedRouteSnapshot } from '@angular/router'
import { RouteTabConfig, RouteData } from './models/route-tab-config.model'
import { MenuTitleService } from './menu-title.service'

@Injectable({
  providedIn: 'root'
})
export class RouteMetadataService {
  private router = inject(Router)
  private routeConfigCache = new Map<string, RouteTabConfig | null>()
  private menuTitleService = inject(MenuTitleService)

  /**
   * Get tab configuration from current activated route
   */
  getCurrentTabConfig(): RouteTabConfig | null {
    const currentRoute = this.router.routerState.root.snapshot
    return this.extractTabConfig(currentRoute)
  }

	/**
	 * Get tab configuration for a specific route path
	 */
	getTabConfigForPath(routePath: string): RouteTabConfig | null {
		const cleanPath = this.cleanRoutePath(routePath)

		// Check cache first
		if (this.routeConfigCache.has(cleanPath)) {
			return this.routeConfigCache.get(cleanPath)!
		}

		let tabConfig: RouteTabConfig | null = null

		// Handle specific known routes first
		if (cleanPath.includes('lms-plus/panel/dashboard')) {
			tabConfig = { title: 'داشبورد ادمین', requiresTab: true, tabGroup: 'main' }
		} else if (cleanPath.includes('lms-plus/panel/menu')) {
			tabConfig = { title: 'منو', requiresTab: true, tabGroup: 'main' }
		} else if (cleanPath.includes('lms-plus/panel/users')) {
			tabConfig = { title: 'کاربران', requiresTab: true, tabGroup: 'users' }
		} else if (cleanPath.includes('lms-plus/panel/organization')) {
			tabConfig = { title: 'سازمان', requiresTab: true, tabGroup: 'admin' }
		} else if (cleanPath.includes('lms-plus/panel/online-users')) {
			tabConfig = { title: 'کاربران آنلاین', requiresTab: true, tabGroup: 'users' }
		}

		// Cache the result
		this.routeConfigCache.set(cleanPath, tabConfig)

		return tabConfig
	}

	/**
	 * Clean route path by removing query parameters and hash
	 */
	cleanRoutePath(routePath: string): string {
		// Handle hash routing - remove #/ prefix
		let cleanPath = routePath
		if (cleanPath.startsWith('#/')) {
			cleanPath = cleanPath.substring(2)
		}

		// Remove query parameters
		cleanPath = cleanPath.split('?')[0]

		return cleanPath
	}

  /**
   * Extract tab configuration from route snapshot recursively
   */
  private extractTabConfig(route: ActivatedRouteSnapshot): RouteTabConfig | null {
    // Check current route data
    const routeData = route.data as RouteData
    if (routeData.tab) {
      return routeData.tab
    }

    // Check parent routes
    if (route.parent) {
      return this.extractTabConfig(route.parent)
    }

    return null
  }

	/**
	 * Find route configuration by path
	 */
	private findRouteConfig(routes: any[], path: string): any {
		for (const route of routes) {
			// Handle dynamic routes with parameters
			if (this.routeMatches(route.path, path)) {
				return route
			}

			// Check loadChildren routes
			if (route.loadChildren && path.includes(route.path || '')) {
				// For lazy loaded modules, we need to check if path starts with route path
				return route
			}

			if (route.children) {
				const childConfig = this.findRouteConfig(route.children, path)
				if (childConfig) return childConfig
			}
		}
		return null
	}

	/**
	 * Check if route path matches the given path (supports parameters)
	 */
	private routeMatches(routePath: string, actualPath: string): boolean {
		if (routePath === '**') return true
		if (routePath === actualPath) return true

		// Handle parameterized routes like ':id', ':userId', etc.
		const routeSegments = routePath.split('/')
		const pathSegments = actualPath.split('/')

		if (routeSegments.length !== pathSegments.length) return false

		return routeSegments.every((segment, index) => {
			return segment.startsWith(':') || segment === pathSegments[index]
		})
	}

   /**
     * Check if a route requires a tab
     */
   requiresTab(routePath: string): boolean {
     // console.log('RouteMetadataService: requiresTab called with:', routePath)

     // Root route should never have a tab
     if (routePath === '/' || routePath === '') {
       // console.log('RouteMetadataService: root route does not require tab:', routePath)
       return false
     }

     const cleanPath = this.cleanRoutePath(routePath)

     // If it's an excluded route, it doesn't need a tab
     if (this.isExcludedRoute(routePath)) {
       return false
     }

     const config = this.getTabConfigForPath(routePath)
     const requiresTab = config?.requiresTab !== false // Default to true unless explicitly false
     // console.log('RouteMetadataService: requiresTab result for', routePath, ':', requiresTab)
     return requiresTab
   }

  /**
   * Get tab title for a route
   */
  getTabTitle(routePath: string): string {
    // First try to get title from menu items
    const menuTitle = this.menuTitleService.getTitleForRoute(routePath)
    if (menuTitle) {
      // console.log('RouteMetadataService: Using menu title', { routePath, menuTitle })
      return menuTitle
    }

    const config = this.getTabConfigForPath(routePath)
    if (config?.title) {
      // console.log('RouteMetadataService: Using config title', { routePath, title: config.title })
      return config.title
    }

    // Fallback to route data title
    const routeConfig = this.findRouteConfig(this.router.config, routePath)
    const fallbackTitle = routeConfig?.data?.['title'] || 'Unknown Page'
    // console.log('RouteMetadataService: Using fallback title', { routePath, title: fallbackTitle })
    return fallbackTitle
  }

	/**
	 * Check if route should be excluded from tab management
	 */
	isExcludedRoute(route: string): boolean {
		const cleanPath = this.cleanRoutePath(route)

		// console.log('RouteMetadataService: isExcludedRoute - original route:', route, 'cleanPath:', cleanPath)

		const excludedRoutes = ['login', 'error', '404', '', 'lms-plus']
		const excludedPrefixes = ['lms-plus/panel/online-users', 'lms-plus/panel/users', 'lms-plus/panel/organization']

		// Check exact matches
		if (excludedRoutes.some(excluded => cleanPath === excluded)) {
			// console.log('RouteMetadataService: route excluded by exact match:', cleanPath)
			return true
		}

		// Also check if original route is root
		if (route === '/' || route === '') {
			// console.log('RouteMetadataService: root route excluded:', route)
			return true
		}

		// Check specific prefixes that should be excluded
		if (excludedPrefixes.some(prefix => cleanPath.startsWith(prefix))) {
			// console.log('RouteMetadataService: route excluded by prefix:', cleanPath)
			return true
		}

		// console.log('RouteMetadataService: route not excluded:', route)
		return false
	}

	/**
	 * Clear route configuration cache (useful for testing or route updates)
	 */
	clearCache(): void {
		this.routeConfigCache.clear()
	}

	/**
	 * Get cache size for monitoring memory usage
	 */
	getCacheSize(): number {
		return this.routeConfigCache.size
	}
}